import PropTypes from "prop-types";
import React from "react";
import {
  CalendarIcon,
  CheckmarkCircleIcon,
  CloseIcon,
  MicOffIcon,
  QuestionCircleIcon,
  SettingsIcon,
} from "@fluentui/react-icons-northstar";
import { Button, Text } from "@fluentui/react-northstar";
import Waveform from "./Waveform";
import { stripWakeWordsForDisplay } from "../utils/textFormatters";

const WidgetContent = ({
  currentStep,
  meterBars,
  listening,
  showCommands,
  interimTranscript,
  speechTranscript,
  transcript,
  awaitWakeWord,
  feedbackMessage,
  compactMode,
  onToggleCompactMode,
  onToggleShowCommands,
  onToggleVoiceReply,
  onToggleAutoConfirm,
  voiceReplyEnabled,
  autoConfirm,
  onOpenCommandHelp,
  onOpenCommandLog,
  t,
  handleNoClick,
  handleYesClick,
  isNoProcessing,
}) => {
  const renderListening = () => (
    <div className="cue-content">
      <div className="waveform-with-settings">
        <Waveform meterBars={meterBars} listening={listening} />
        <div className="settings-buttons">
          <Button
            icon={<QuestionCircleIcon />}
            className="command-log-button"
            onClick={onOpenCommandHelp}
            title={t("whatCanISay")}
            circular
            size="small"
          />
          <Button
            icon={<CalendarIcon />}
            className="command-log-button"
            onClick={onOpenCommandLog}
            title="View command log"
            circular
            size="small"
          />
          <Button
            icon={<SettingsIcon />}
            className="settings-button"
            onClick={onToggleCompactMode}
            title={compactMode ? "Show settings" : "Hide settings"}
            circular
            size="small"
          />
        </div>
      </div>

      {showCommands && (
        <div className="transcription-area">
          {(() => {
            const displayText = stripWakeWordsForDisplay(
              interimTranscript || speechTranscript || transcript
            );
            return displayText ? <Text content={displayText} size="medium" /> : null;
          })()}
        </div>
      )}
      <Text
        content={
          currentStep === "listening"
            ? awaitWakeWord
              ? 'Say "Hi Assistant"'
              : "Listening..."
            : "Processing..."
        }
        size="small"
        className="listening-text"
      />
      {currentStep === "listening" && feedbackMessage && (
        <Text content={feedbackMessage} size="small" className="hint-text" />
      )}

      {!compactMode && (
        <div className="preferences-panel one-col">
          <div className="pref-row">
            <div className="left-cell">
              <span className="pref-label">Show commands</span>
              <Button
                content={showCommands ? "ON" : "OFF"}
                className={`control-button pref-toggle ${showCommands ? "on" : "off"}`}
                primary={showCommands}
                onClick={onToggleShowCommands}
                title="Toggle show commands"
              />
            </div>
          </div>
          <div className="pref-row">
            <div className="left-cell">
              <span className="pref-label">Avatar voice reply</span>
              <Button
                content={voiceReplyEnabled ? "ON" : "OFF"}
                className={`control-button pref-toggle ${
                  voiceReplyEnabled ? "on" : "off"
                }`}
                primary={voiceReplyEnabled}
                onClick={onToggleVoiceReply}
                title="Toggle voice reply"
              />
            </div>
          </div>
          <div className="pref-row">
            <div className="left-cell">
              <span className="pref-label">Auto-confirm</span>
              <Button
                content={autoConfirm ? "ON" : "OFF"}
                className={`control-button pref-toggle ${autoConfirm ? "on" : "off"}`}
                primary={autoConfirm}
                onClick={onToggleAutoConfirm}
                title="Toggle auto confirm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPaused = () => (
    <div className="cue-content">
      <div className="paused-state" style={{ textAlign: "center", padding: "20px" }}>
        <MicOffIcon
          style={{
            fontSize: "48px",
            color: "#6264a7",
            marginBottom: "16px",
          }}
        />
        <Text
          content="Microphone paused"
          size="medium"
          style={{ display: "block", marginBottom: "8px" }}
        />
        <Text
          content="Click play to continue listening"
          size="small"
          style={{ display: "block", color: "#666" }}
        />
      </div>
    </div>
  );

  const renderConfirming = () => (
    <div className="cue-content">
      {showCommands && (
        <div className="transcription-area">
          {(() => {
            const displayText = stripWakeWordsForDisplay(transcript);
            return displayText ? <Text content={displayText} size="medium" /> : <Text content="" size="medium" />;
          })()}
        </div>
      )}
      <Text
        content="Do you want to make changes in the transcription?"
        size="small"
        className="confirmation-question"
      />
      <div className="confirmation-buttons">
        <Button
          content={isNoProcessing ? "Processing..." : "No"}
          onClick={isNoProcessing ? undefined : handleNoClick}
          className={`no-button ${isNoProcessing ? "loading" : ""}`}
          disabled={isNoProcessing}
          loading={isNoProcessing}
        />
        <Button
          content="Yes"
          primary
          onClick={isNoProcessing ? undefined : handleYesClick}
          className="yes-button success-button"
          icon={<CheckmarkCircleIcon />}
          disabled={isNoProcessing}
        />
      </div>
    </div>
  );

  const renderProcessing = () => (
    <div className="cue-content">
      <Waveform meterBars={meterBars} listening={listening} />
      {showCommands && (
        <div className="transcription-area">
          <Text content={transcript} size="medium" />
        </div>
      )}
      <Text content={feedbackMessage} size="small" className="processing-text" />
    </div>
  );

  const renderStatus = ({ icon, text, className }) => (
    <div className="cue-content">
      <div className={className}>
        {icon}
        <Text content={text} size="medium" className={`${className}-text`} />
      </div>
    </div>
  );

  switch (currentStep) {
    case "listening":
    case "transcribing":
      return renderListening();
    case "paused":
      return renderPaused();
    case "confirming":
      return renderConfirming();
    case "processing":
      return renderProcessing();
    case "success":
      return renderStatus({
        icon: (
          <CheckmarkCircleIcon
            style={{
              fontSize: "48px",
              color: "#28a745",
              marginBottom: "16px",
            }}
          />
        ),
        text: feedbackMessage,
        className: "success-state",
      });
    case "error":
      return renderStatus({
        icon: (
          <CloseIcon
            style={{
              fontSize: "48px",
              color: "#dc3545",
              marginBottom: "16px",
            }}
          />
        ),
        text: feedbackMessage,
        className: "error-state",
      });
    default:
      return null;
  }
};

WidgetContent.propTypes = {
  autoConfirm: PropTypes.bool.isRequired,
  awaitWakeWord: PropTypes.bool.isRequired,
  compactMode: PropTypes.bool.isRequired,
  currentStep: PropTypes.string.isRequired,
  feedbackMessage: PropTypes.string.isRequired,
  handleNoClick: PropTypes.func.isRequired,
  handleYesClick: PropTypes.func.isRequired,
  interimTranscript: PropTypes.string.isRequired,
  isNoProcessing: PropTypes.bool.isRequired,
  listening: PropTypes.bool.isRequired,
  meterBars: PropTypes.arrayOf(PropTypes.number).isRequired,
  onOpenCommandHelp: PropTypes.func.isRequired,
  onOpenCommandLog: PropTypes.func.isRequired,
  onToggleAutoConfirm: PropTypes.func.isRequired,
  onToggleCompactMode: PropTypes.func.isRequired,
  onToggleShowCommands: PropTypes.func.isRequired,
  onToggleVoiceReply: PropTypes.func.isRequired,
  showCommands: PropTypes.bool.isRequired,
  speechTranscript: PropTypes.string.isRequired,
  transcript: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  voiceReplyEnabled: PropTypes.bool.isRequired,
};

export default WidgetContent;

